(() => {
  'use strict';
  if (window.OptimalRequests) return;
  const pending = new Map();
  const cooldowns = new Map();
  const diagnostics = [];
  const safeUrl = (value, image = false) => {
    try {
      const url = new URL(String(value || ''), location.origin);
      if (!value || !['http:', 'https:'].includes(url.protocol) || url.username || url.password) return null;
      if (!image && url.origin !== location.origin) return null;
      if (location.protocol === 'https:' && url.protocol !== 'https:') return null;
      return url.href;
    } catch (_) { return null; }
  };
  const record = (kind, operation, status = 0) => {
    // Deliberately exclude URLs, search terms, form contents, identities and error stacks.
    diagnostics.push({ kind, operation: ['cart', 'search', 'theme'].includes(operation) ? operation : 'other', status, at: Date.now() });
    if (diagnostics.length > 30) diagnostics.shift();
  };
  const json = (path, options = {}, settings = {}) => {
    const target = safeUrl(path);
    if (!target) return Promise.reject(new Error('Only shop requests are allowed.'));
    const url = new URL(target);
    const method = (options.method || 'GET').toUpperCase();
    const write = method !== 'GET';
    const key = `${method}:${url.pathname}`;
    const flightKey = `${key}${url.search}`;
    const operation = settings.operation || 'other';
    const error = (message, kind, status = 0, uncertain = false) => Object.assign(new Error(message), { kind, status, uncertain });
    if ((cooldowns.get(key) || 0) > Date.now()) {
      return Promise.reject(error('Too many requests. Please wait a moment before trying again.', 'rate-limit', 429));
    }
    if (!write && !options.signal && pending.has(flightKey)) return pending.get(flightKey);
    if (write) pending.clear();
    const run = async () => {
      const controller = new AbortController();
      const abort = () => controller.abort();
      let timedOut = false;
      let response;
      if (options.signal?.aborted) controller.abort();
      options.signal?.addEventListener('abort', abort, { once: true });
      const timer = setTimeout(() => { timedOut = true; controller.abort(); }, settings.timeoutMs || 12000);
      try {
        response = await fetch(url.href, { ...options, credentials: 'same-origin', redirect: 'error', signal: controller.signal });
        if (response.status === 429) {
          const header = response.headers.get('Retry-After');
          const seconds = header && /^\d+$/.test(header) ? Number(header) : (Date.parse(header) - Date.now()) / 1000;
          cooldowns.set(key, Date.now() + Math.max(5, Number.isFinite(seconds) ? seconds : 30) * 1000);
          if (cooldowns.size > 30) cooldowns.delete(cooldowns.keys().next().value);
          throw error('Too many requests. Please wait a moment before trying again.', 'rate-limit', 429);
        }
        let data;
        try { data = await response.json(); }
        catch (cause) {
          if (controller.signal.aborted) throw cause;
          throw error('The shop returned an unreadable response.', 'invalid-response', response.status, write);
        }
        if (!response.ok) throw error(
          response.status === 422 && typeof data.description === 'string' ? data.description : 'The shop could not complete this request. Please try again later.',
          'http', response.status, write && response.status >= 500
        );
        return data;
      } catch (cause) {
        if (options.signal?.aborted && !timedOut) throw cause;
        const failure = cause.kind ? cause : error(timedOut ? 'The request took too long. Please check your connection.' : 'Connection interrupted. Please check your connection.', timedOut ? 'timeout' : 'network', response?.status || 0, write);
        record(failure.kind, operation, failure.status);
        throw failure;
      } finally {
        clearTimeout(timer);
        options.signal?.removeEventListener('abort', abort);
      }
    };
    const promise = run();
    if (!write && !options.signal) {
      pending.set(flightKey, promise);
      const clear = () => { if (pending.get(flightKey) === promise) pending.delete(flightKey); };
      promise.then(clear, clear);
    }
    return promise;
  };
  const validateCart = cart => {
    const integer = n => Number.isSafeInteger(n) && n >= 0;
    if (!cart || !Array.isArray(cart.items) || !integer(cart.item_count) || !integer(cart.total_price)
      || !cart.items.every(item => item && typeof item.key === 'string' && integer(item.quantity) && integer(item.final_line_price))) {
      record('invalid-cart', 'cart');
      throw Object.assign(new Error('Your cart could not be read. Please open the cart page.'), { uncertain: true });
    }
    return cart;
  };
  window.OptimalRequests = { json, safeUrl, validateCart, diagnostics: () => diagnostics.map(entry => ({ ...entry })) };
  window.addEventListener('error', event => {
    // Only classify errors sourced from our theme filenames. Never store the message or URL.
    try {
      const file = new URL(event.filename).pathname.split('/').pop();
      if (/^(theme|storefront|request-safety|product-experience|product-options|panel-carousel|home-motion|design-archive|discovery|shopping-tools|select-menu)\.js$/.test(file)) record('script-error', 'theme');
    } catch (_) { /* Unattributed and third-party errors are intentionally ignored. */ }
  });

  // Native Shopify contact/newsletter forms retain their normal POST and CAPTCHA flow.
  const guarded = new Map();
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!form.matches('.contact-form, .newsletter-form, .password-page__form') || event.defaultPrevented) return;
    if (!['contact', 'customer'].includes(form.querySelector('[name="form_type"]')?.value)) return;
    if (guarded.has(form)) { event.preventDefault(); return; }
    const token = {};
    guarded.set(form, token);
    form.setAttribute('aria-busy', 'true');
    const buttons = [...form.querySelectorAll('[type="submit"]')];
    buttons.forEach(button => button.setAttribute('aria-disabled', 'true'));
    const release = () => {
      if (guarded.get(form) !== token) return;
      guarded.delete(form);
      form.removeAttribute('aria-busy');
      buttons.forEach(button => button.removeAttribute('aria-disabled'));
    };
    // Recover if a challenge or another integration cancels navigation.
    setTimeout(release, 15000);
    queueMicrotask(() => { if (event.defaultPrevented) release(); });
  });
  window.addEventListener('pageshow', () => {
    guarded.forEach((token, form) => {
      form.removeAttribute('aria-busy');
      form.querySelectorAll('[aria-disabled]').forEach(button => button.removeAttribute('aria-disabled'));
    });
    guarded.clear();
  });
})();
